import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartDataSets, ChartOptions } from "chart.js";
import { BaseChartDirective, Color, Label } from "ng2-charts";
import { SubSink } from "subsink";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Configuration,
  RocePoint,
  ProductRoceChartDatasource,
} from "@/shared-module/models";
import { ConfigurationService } from "@/shared-module/services";
import * as pluginAnnotations from "chartjs-plugin-annotation";

@Component({
  selector: "app-decisionproductrocechart",
  templateUrl: "decisionproductrocechart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class DecisionProductRoceChartComponent implements OnInit {
  @ViewChild("template", { static: true }) template;
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  initialized: boolean;
  model: ProductRoceChartDatasource;
  rgbColor: string = "#FFFFFF";
  subs = new SubSink();
  @Input("data") set setdynamicvalues(data: ProductRoceChartDatasource) {
    if (data) {
      this.changeValues(data);
    }
  }

  public lineChartData: ChartDataSets[] = [];
  public lineChartLabels: Label[] = [];
  public lineChartColors: Color[] = [];
  public lineChartLegend = true;
  public lineChartType = "line";
  public chartPlugins = [pluginAnnotations, ChartDataLabels];

  minY = 0.0;
  maxY = 3.0;
  minX = 0;
  maxX = 0.25;

  public lineChartOptions: ChartOptions & { annotation: any } = {
    aspectRatio: 2,
    responsive: true,
    annotation: {
      annotations: [
        {
          type: "line",
          mode: "vertical",
          scaleID: "x-axis-0",
          value: "0",
          borderColor: "#FFFFFF",
          borderWidth: 2,
        },
      ],
    },
    plugins: {
      datalabels: {
        display: true,
        /*anchor(context) {
          const value = context.dataset.data[context.dataIndex];
          // tslint:disable-next-line:no-string-literal
          return value['r'] < 16 ? 'end' : 'center';
        },*/
        align(context) {
          // tslint:disable-next-line:no-string-literal
          return "end";
        },
        color(context) {
          const value = context.dataset.data[context.dataIndex];
          return value["color"];
        },
        font: {
          weight: "bold",
        },
        formatter(value, context) {
          // tslint:disable-next-line:no-string-literal
          if (context.dataset.data.length === 1) return value.text;
          return null;
        },
        offset: 2,
        padding: 0,
      },
    },
    tooltips: {
      // Disable the on-canvas tooltip
      enabled: true,
      callbacks: {
        title: (items, data) => {
          if (items[0].datasetIndex === 0)
            return (
              data.datasets[items[0].datasetIndex].data[items[0].index][
                "serieName"
              ] +
              ": " +
              data.datasets[items[0].datasetIndex].data[items[0].index]["text"]
            );
          else if (items[0].datasetIndex === 1)
            return (
              data.datasets[items[0].datasetIndex].data[items[0].index][
                "serieName"
              ] +
              ": " +
              data.datasets[items[0].datasetIndex].data[items[0].index]["text"]
            );
          return null;
        },
        label: (tooltipItem, data) => {
          if (
            tooltipItem.datasetIndex === 0 ||
            tooltipItem.datasetIndex === 1
          ) {
            var x =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index][
                "x"
              ];
            var y =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index][
                "y"
              ];
            return [
              this.model?.isoRoceXAxisLabel +
                ": " +
                Math.round(x * 1000) / 10 +
                "%",
              this.model?.isoRoceYAxisLabel + ": " + Math.round(y * 100) / 100,
            ];
          }
          return null;
        },
      },
    },
    scales: {
      gridLines: {
        drawBorder: false,
        color: "#444444",
        display: true,
      },
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          type: "linear",
          scaleLabel: {
            display: true,
            fontColor: "#EEEEEE",
          },
          gridLines: {
            drawBorder: false,
            color: "#444444",
            display: true,
          },
          ticks: {
            beginAtZero: true,
            fontColor: "#FFFFFF",
            stepSize: 0.05,
            max: 0.25,
            min: 0,
            callback: function (value: number, index, values) {
              return Math.round(value * 100) + "%";
            },
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          type: "linear",
          scaleLabel: {
            display: true,
            fontColor: "#EEEEEE",
          },
          gridLines: {
            zeroLineColor: "#ffffff",
            zeroLineWidth: 2,
            drawBorder: false,
            color: "#444444",
            display: true,
          },
          ticks: {
            fontColor: "#FFFFFF",
            min: 0,
            stepSize: 0.2,
            max: 2.6,
          },
        },
      ],
    },
  };

  constructor(
    private configurationService: ConfigurationService,
    private viewContainerRef: ViewContainerRef,
  ) {
    const thisObject = this;
    this.subs.sink = this.configurationService.config.subscribe(
      (c: Configuration) => {
        thisObject.rgbColor = c.rgb;
      },
    );
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);
  }

  forceUpdate(): void {
    if (this.chart !== undefined) {
      this.chart.ngOnDestroy();
      this.chart.chart = this.chart.getChartBuilder(this.chart.ctx);
    }
  }

  changeValues(data: ProductRoceChartDatasource): void {
    if (JSON.stringify(this.model) === JSON.stringify(data))
      // don't refresh if data is the same !
      return;

    this.model = data;
    let needForceUpdate = false;

    if (
      this.lineChartOptions.scales.xAxes[0].scaleLabel.labelString == null &&
      data?.isoRoceXAxisLabel != null
    ) {
      this.lineChartOptions.scales.xAxes[0].scaleLabel.labelString =
        data.isoRoceXAxisLabel;
      this.lineChartOptions.scales.yAxes[0].scaleLabel.labelString =
        data.isoRoceYAxisLabel;

      needForceUpdate = true;
    }

    if (!this.initialized) {
      this.lineChartData = [];
      this.lineChartColors = [];

      this.lineChartData.push(this.getPoint(data.forecast));
      this.lineChartData.push(this.getPoint(data.current));
      this.lineChartData.push(this.getDataSet(0.05));
      this.lineChartData.push(this.getDataSet(0.1));
      this.lineChartData.push(this.getDataSet(0.15));

      this.lineChartColors.push(this.getColor(this.rgbColor));
      this.lineChartColors.push(this.getColor("#EEEEEE"));
      this.lineChartColors.push(this.getColor("#CCCCCC"));
      this.lineChartColors.push(this.getColor("#AAAAAA"));
      this.lineChartColors.push(this.getColor("#888888"));

      this.initialized = true;
    }

    this.lineChartData[0].data[0] = data.forecast;
    this.lineChartData[1].data[0] = data.current;

    var minY = Math.min(data.forecast.y, data.current.y);
    var maxY = Math.max(data.forecast.y, data.current.y);
    var minX = Math.min(data.forecast.x, data.current.x);
    var maxX = Math.max(data.forecast.x, data.current.x);

    const maxAbsY = Math.max(Math.abs(minY), Math.abs(maxY));

    minX = Math.min(0, minX);
    maxX = Math.max(0.25, maxX);

    var optimalMinX = minX;
    var optimalMaxX = maxX;
    var optimalMinY = 0;
    var optimalMaxY = 0;

    if (minY < 0) optimalMinY = -maxAbsY;
    else optimalMinY = 0;

    if (maxY > 0) optimalMaxY = maxAbsY;
    else optimalMaxY = 0;

    if (
      optimalMinY < this.minY ||
      optimalMaxY > this.maxY ||
      optimalMaxX > this.maxX ||
      optimalMinX < this.minX
    ) {
      this.minY = Math.floor(optimalMinY * 1.05);
      this.maxY = Math.ceil(optimalMaxY * 1.05);
      this.minX = this.getRoundedXAxeValue(optimalMinX * 1.05);
      this.maxX = this.getRoundedXAxeValue(optimalMaxX * 1.05);

      this.lineChartOptions.scales.xAxes[0].ticks.min = this.minX;
      this.lineChartOptions.scales.xAxes[0].ticks.max = this.maxX;
      this.lineChartOptions.scales.yAxes[0].ticks.min = this.minY;
      this.lineChartOptions.scales.yAxes[0].ticks.max = this.maxY;

      this.lineChartData = [];
      this.lineChartData.push(this.getPoint(data.forecast));
      this.lineChartData.push(this.getPoint(data.current));
      this.lineChartData.push(this.getDataSet(0.05));
      this.lineChartData.push(this.getDataSet(0.1));
      this.lineChartData.push(this.getDataSet(0.15));

      needForceUpdate = true;
    }

    if (needForceUpdate) {
      this.forceUpdate();
    } else {
      this.chart?.update();
    }
  }

  getRoundedXAxeValue(value: number): number {
    var modulo5 = Math.abs(Math.round(value * 100)) % 5;
    var rounded = Math.abs(Math.round(value * 100)) + (5 - modulo5);
    var ret = rounded / 100.0;
    if (value < 0) return -ret;
    return ret;
  }

  getRoundedYAxeValue(value: number): number {
    return Math.ceil(value);
  }

  getColor(color: string): any {
    return {
      // grey
      backgroundColor: color,
      borderColor: color,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: color,
      borderWidth: 1,
    };
  }

  getDataSet(roce: number): any {
    const minX = 0;
    const maxX = this.maxX;

    const nbSteps = 100;
    const serie = [];

    for (let i = 0; i < nbSteps; i++) {
      const x = minX + ((maxX - minX) / nbSteps) * i;
      if (x > 0) {
        const y = roce / x;
        serie.push({ x: x, y: y });
      }
    }

    // tslint:disable-next-line:object-literal-shorthand
    return {
      data: serie,
      label: this.model?.isoRoceTitle + " " + roce * 100 + "%",
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
    };
  }

  getPoint(data: RocePoint): any {
    const serie = [data];

    // tslint:disable-next-line:object-literal-shorthand
    return { data: serie, label: data.serieName, fill: false, pointRadius: 5 };
  }
}
